import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export const date = dayjs.utc;

export type DatesRange = { start: Date; end: Date };

export const getMinutesFromDate = (value: Date) => {
  const dv = date(value);
  return dv.hour() * 60 + dv.minute();
};

// const getDateFromMinutes = (minutes:Date) => {

// }

export default class DatesUtils {
  static isValidDate(date: Date): boolean {
    return (
      date &&
      Object.prototype.toString.call(date) === '[object Date]' &&
      !isNaN(date.getTime())
    );
  }

  static getDiffInDays(date1: Date, date2: Date): number {
    const diffTime = Math.abs(date2.getTime() - date1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  static addYears(date: Date, years: number): Date {
    return new Date(
      date.getFullYear() + years,
      date.getMonth(),
      date.getDate()
    );
  }

  static addMonths(date: Date, months: number): Date {
    return new Date(
      date.getFullYear(),
      date.getMonth() + months,
      date.getDate()
    );
  }

  static addDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  }

  static subtractYears(date: Date, years: number): Date {
    return new Date(
      date.getFullYear() - years,
      date.getMonth(),
      date.getDate()
    );
  }

  static subtractMonths(date: Date, months: number): Date {
    return new Date(
      date.getFullYear(),
      date.getMonth() - months,
      date.getDate()
    );
  }

  static subtractDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - days);
  }

  static sameDate(a: Date, b: Date): boolean {
    return (
      a.getDate() === b.getDate() &&
      a.getMonth() === b.getMonth() &&
      a.getFullYear() === b.getFullYear()
    );
  }

  static niceDate(
    date: Date,
    year = true,
    month = true,
    day = true,
    time = false
  ): string {
    const format = `${day ? 'D' : ''}${month ? ' MMM' : ' '}${
      year ? ' YYYY' : ''
    }${time ? ' - HH:mm' : ''}`;
    return dayjs(date).format(format);
  }

  static getTomorrow(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return DatesUtils.addDays(today, 1);
  }

  static niceTime(duration: number): string {
    const dv = this.dateFromMinutes(duration);
    return date(dv).format('HH:mm');
  }

  static monthsDiff(d1: Date, d2: Date) {
    const years = this.yearsDiff(d1, d2);
    return years * 12 + (d2.getMonth() - d1.getMonth());
  }

  static yearsDiff(d1: Date, d2: Date) {
    return d2.getFullYear() - d1.getFullYear();
  }

  // index 1 = months difference, index 2 = days difference
  static monthsDaysDiff(d1: Date, d2: Date): number[] {
    const months = this.monthsDiff(d1, d2);

    const dateIn = new Date(d1);
    const dayIn = dateIn.getDate();
    const daysInMonth = new Date(
      dateIn.getFullYear(),
      dateIn.getMonth() + 1,
      0
    ).getDate();
    const dayOut = new Date(d2).getDate();

    if (dayIn === dayOut) {
      return [months, 0];
    }

    if (dayIn > dayOut) {
      const days = daysInMonth - dayIn + dayOut;
      return [months - 1, days];
    }

    return [months, dayOut - dayIn];
  }

  // return an array of strings containing the months from now to one year back
  static getOneYearBack(): string[] {
    const months = [];
    const currentMonth = new Date().getMonth() + 1;

    for (let i = currentMonth + 1; i <= 12; i++) {
      months.push(this.getMonthLabel(i));
    }

    for (let i = 1; i <= currentMonth; i++) {
      months.push(this.getMonthLabel(i));
    }

    return months;
  }

  // return an array of strings containing the months from now to the next x nonths or
  // from now to the previous y months
  static getNextPreviousMonths(
    numNext?: number,
    numPrevious?: number
  ): string[] {
    const months = [];
    let currentMonth = new Date().getMonth() + 1;

    if (numPrevious && currentMonth - numPrevious > 0) {
      currentMonth = currentMonth - numPrevious;
    } else if (numPrevious && currentMonth - numPrevious <= 0) {
      currentMonth = currentMonth - numPrevious + 12;
    }

    if (numPrevious) {
      for (let i = 0; i < numPrevious; i++) {
        if (currentMonth <= 12) {
          months.push(this.getMonthLabel(currentMonth));
        } else {
          currentMonth = 1;
          months.push(this.getMonthLabel(currentMonth));
        }
        currentMonth++;
      }
    }

    currentMonth = new Date().getMonth() + 1;

    if (numNext) {
      for (let i = 0; i <= numNext; i++) {
        if (currentMonth <= 12) {
          months.push(this.getMonthLabel(currentMonth));
        } else {
          currentMonth = 1;
          months.push(this.getMonthLabel(currentMonth));
        }
        currentMonth++;
      }
    }

    return months;
  }

  static getMonthLabel(num: number): string {
    switch (num) {
      case 1:
        return 'Jan';
      case 2:
        return 'Feb';
      case 3:
        return 'Mar';
      case 4:
        return 'Apr';
      case 5:
        return 'May';
      case 6:
        return 'Jun';
      case 7:
        return 'Jul';
      case 8:
        return 'Aug';
      case 9:
        return 'Sep';
      case 10:
        return 'Oct';
      case 11:
        return 'Nov';
      case 12:
        return 'Dec';
    }
    return 'Unknown';
  }

  // This calculates the minimum dates ranges array to represent a set of other ranges
  // ( possibly overlapping )
  static getMinifiedRanges(initialRanges: DatesRange[]): DatesRange[] {
    const daysBooked: { [key: string]: boolean } = {};

    for (const br of initialRanges) {
      const { start, end } = br;
      const dstart = date(start);
      const dend = date(end);
      const days: Date[] = [];
      for (let i = dstart; i <= dend; i.add(1, 'day')) {
        days.push(i.toDate());
      }
      for (const day of days) {
        const exd = day.setHours(0, 0, 0, 0);
        daysBooked[exd.toString()] = true;
      }
    }

    const datesBooked: number[] = Object.keys(daysBooked)
      .map((v) => parseInt(v, 10))
      .sort((a, b) => (a < b ? -1 : 1));

    const ranges: DatesRange[] = [];

    const db = datesBooked;
    let start = db[0];
    for (let i = 1; i < db.length; i++) {
      if (i === db.length - 1) {
        ranges.push({ start: new Date(start), end: new Date(db[i]) });
        break;
      }

      const prev = db[i];
      const next = db[i + 1];
      const pd = new Date(prev);

      if (next !== date(prev).add(1, 'day').toDate().getTime()) {
        ranges.push({ start: new Date(start), end: new Date(pd) });

        start = db[i + 1];
      }
    }

    return ranges;
  }

  static getMinutesFromMillis(millis: number): number {
    const date = new Date(millis);
    return date.getHours() * 60 + date.getMinutes();
  }

  static dateFromMinutes(mins: number): Date {
    const hours = Math.floor(mins / 60);
    const d = date()
      .hour(hours)
      .minute(mins - hours * 60);
    return d.toDate();
  }

  static roundedToDay(millis: number): number {
    const date = new Date(millis);
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return day.getTime();
  }
}

export const isDateString = (value: string) =>
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/.test(
    value
  );
